<script setup lang="ts">
import { mergeRegister } from "@lexical/utils";
import { type LexicalNode, type RangeSelection } from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { computed, onMounted, onUnmounted, ref } from "vue";

import { notify } from "~/components/notifications";
import { EVENT_GENERATE_RECOMMENDATION } from "~/components/text/const";
import { getRecommendationTypeaheadSections } from "~/components/text/utils";
import { NotificationType } from "~/shared/enums";
import type { TypeaheadGetOptionsFn, TypeaheadOption } from "~/shared/types";
import { usePageStore } from "~/stores";
import { isLexicalStateEmpty } from "~/utils/common";

import TypeaheadPlugin from "./TypeaheadPlugin.vue";

const props = defineProps<{
  title: string;
}>();

const pageStore = usePageStore();
const editor = useLexicalComposer();

const editorEmpty = ref(false);

const getOptionsFns = computed<TypeaheadGetOptionsFn[]>(() => {
  const sections = getRecommendationTypeaheadSections(editor._config.namespace, true, editorEmpty.value);
  return sections.map((section) => () => Promise.resolve(section));
});

const onSelect = (_: RangeSelection, option: TypeaheadOption, parentNode: LexicalNode | null) => {
  if (props.title === "") {
    notify({
      message: "Please enter a title first.",
      type: NotificationType.WARNING,
    });
    return;
  }

  editor.dispatchCommand(EVENT_GENERATE_RECOMMENDATION, {
    kind: option.value,
    parentKey: parentNode?.getKey(),
  });
};

let unregisterListeners: () => void;

onMounted(() => {
  unregisterListeners = mergeRegister(
    editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        editorEmpty.value = isLexicalStateEmpty(editorState.toJSON(), true);
      });
    })
  );
});

onUnmounted(() => {
  unregisterListeners?.();
});
</script>

<template>
  <TypeaheadPlugin v-if="pageStore.isOnline" trigger="/" :get-options-fns="getOptionsFns" @select="onSelect" />
</template>
